/**
 * Payment Modal
 * Full-screen modal for selecting payment method and finalizing a sale.
 * Supports: cash, debit card, credit card, PIX, fiado, and mixed payments.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Banknote,
  CreditCard,
  QrCode,
  UserCheck,
  Plus,
  Check,
  Loader2,
  Clock,
  AlertTriangle,
  Printer,
} from 'lucide-react';
import { printReceipt } from '@/lib/printer';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { CustomerSearchModal } from './CustomerSearchModal';

type PaymentMethod = 'dinheiro' | 'cartao_debito' | 'cartao_credito' | 'pix' | 'fiado';

interface PaymentEntry {
  id: string;
  method: PaymentMethod;
  amount: number;
  received_amount?: number;
}

interface PaymentModalProps {
  isOpen: boolean;
  saleId: string | null;
  saleTotal: number;
  onClose: () => void;
  onSuccess: (data: { receipt_number: string; change: number }) => void;
}

interface ReceiptData {
  sale_number: number;
  receipt_number: string;
  total: number;
  change: number;
  payments: Array<{ method: string; amount: number }>;
  type: string;
  items?: Array<{ product_name: string; quantity: number; unit_price: number; subtotal: number; unit: string }>;
  subtotal?: number;
  discount?: number;
}

const METHOD_LABELS: Record<PaymentMethod, string> = {
  dinheiro: 'Dinheiro',
  cartao_debito: 'Cartão Débito',
  cartao_credito: 'Cartão Crédito',
  pix: 'PIX',
  fiado: 'Credito',
};

const METHOD_ICONS: Record<PaymentMethod, React.ReactNode> = {
  dinheiro: <Banknote className="h-6 w-6" />,
  cartao_debito: <CreditCard className="h-6 w-6" />,
  cartao_credito: <CreditCard className="h-6 w-6" />,
  pix: <QrCode className="h-6 w-6" />,
  fiado: <UserCheck className="h-6 w-6" />,
};

export function PaymentModal({ isOpen, saleId, saleTotal, onClose, onSuccess }: PaymentModalProps) {
  const [step, setStep] = useState<'select' | 'cash' | 'card' | 'pix' | 'fiado' | 'mixed' | 'success'>('select');
  const [payments, setPayments] = useState<PaymentEntry[]>([]);
  const [cashReceived, setCashReceived] = useState('');
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pixData, setPixData] = useState<{ payment_id: string; pix_payload: string; expires_in: number } | null>(null);
  const [pixTimer, setPixTimer] = useState(0);
  const [customerSearch, setCustomerSearch] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<{ id: string; name: string; available_credit: number | null } | null>(null);
  const [change, setChange] = useState(0);
  const [receiptNumber, setReceiptNumber] = useState('');
  const [receiptData, setReceiptData] = useState<ReceiptData | null>(null);
  const cashInputRef = useRef<HTMLInputElement>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setPayments([]);
      setCashReceived('');
      setSelectedMethod(null);
      setIsProcessing(false);
      setPixData(null);
      setPixTimer(0);
      setSelectedCustomer(null);
      setChange(0);
      setReceiptNumber('');
      setReceiptData(null);
    }
  }, [isOpen]);

  // Focus cash input when step changes
  useEffect(() => {
    if (step === 'cash') {
      setTimeout(() => cashInputRef.current?.focus(), 100);
    }
  }, [step]);

  // PIX timer countdown
  useEffect(() => {
    if (pixTimer <= 0 || step !== 'pix') return;
    const interval = setInterval(() => {
      setPixTimer((t) => {
        if (t <= 1) {
          clearInterval(interval);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [pixTimer, step]);

  const remainingAmount = saleTotal - payments.reduce((sum, p) => sum + p.amount, 0);

  const formatCurrency = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const handleMethodSelect = useCallback((method: PaymentMethod) => {
    setSelectedMethod(method);
    if (method === 'dinheiro') {
      setStep('cash');
    } else if (method === 'cartao_debito' || method === 'cartao_credito') {
      setStep('card');
    } else if (method === 'pix') {
      setStep('card');
    } else if (method === 'fiado') {
      setStep('fiado');
      setCustomerSearch(true);
    }
  }, []);

  const handleCashPayment = useCallback(async () => {
    const received = parseFloat(cashReceived.replace(',', '.'));
    if (isNaN(received) || received <= 0) {
      toast.error('Informe um valor válido');
      return;
    }

    const amountToPay = payments.length > 0 ? remainingAmount : saleTotal;
    if (received < amountToPay) {
      // For mixed payment, add partial
      if (payments.length > 0 || received < amountToPay) {
        const entry: PaymentEntry = {
          id: Date.now().toString(),
          method: 'dinheiro',
          amount: Math.min(received, amountToPay),
          received_amount: received,
        };
        const newPayments = [...payments, entry];
        setPayments(newPayments);

        const newRemaining = saleTotal - newPayments.reduce((sum, p) => sum + p.amount, 0);
        if (newRemaining <= 0) {
          await finalizeSale(newPayments);
        } else {
          setStep('select');
          setCashReceived('');
        }
        return;
      }
    }

    // Full payment
    const entry: PaymentEntry = {
      id: Date.now().toString(),
      method: 'dinheiro',
      amount: amountToPay,
      received_amount: received,
    };
    const newPayments = [...payments, entry];
    setPayments(newPayments);
    await finalizeSale(newPayments);
  }, [cashReceived, payments, remainingAmount, saleTotal]);

  const handleCardPayment = useCallback(async () => {
    const amountToPay = payments.length > 0 ? remainingAmount : saleTotal;
    const entry: PaymentEntry = {
      id: Date.now().toString(),
      method: selectedMethod as PaymentMethod,
      amount: amountToPay,
    };
    const newPayments = [...payments, entry];
    setPayments(newPayments);
    await finalizeSale(newPayments);
  }, [payments, remainingAmount, saleTotal, selectedMethod]);

  const handlePixGenerate = useCallback(async () => {
    if (!saleId) return;
    const amountToPay = payments.length > 0 ? remainingAmount : saleTotal;

    try {
      const response = await apiFetch('/api/payments/pix/generate', {
        method: 'POST',
        body: JSON.stringify({ sale_id: saleId, amount: amountToPay }),
      });

      if (!response.ok) {
        toast.error('Erro ao gerar PIX');
        return;
      }

      const data = await response.json();
      setPixData(data);
      setPixTimer(data.expires_in || 300);
    } catch {
      toast.error('Erro de conexão');
    }
  }, [saleId, payments, remainingAmount, saleTotal]);

  // Generate PIX when entering pix step
  useEffect(() => {
    if (step === 'pix' && !pixData) {
      handlePixGenerate();
    }
  }, [step, pixData, handlePixGenerate]);

  const handlePixConfirm = useCallback(async () => {
    if (!pixData || !saleId) return;

    try {
      const response = await apiFetch('/api/payments/pix/confirm', {
        method: 'POST',
        body: JSON.stringify({ payment_id: pixData.payment_id, sale_id: saleId }),
      });

      if (!response.ok) {
        toast.error('Erro ao confirmar PIX');
        return;
      }

      const amountToPay = payments.length > 0 ? remainingAmount : saleTotal;
      const entry: PaymentEntry = {
        id: Date.now().toString(),
        method: 'pix',
        amount: amountToPay,
      };
      const newPayments = [...payments, entry];
      setPayments(newPayments);
      await finalizeSale(newPayments);
    } catch {
      toast.error('Erro de conexão');
    }
  }, [pixData, saleId, payments, remainingAmount, saleTotal]);

  const handleFiadoConfirm = useCallback(async () => {
    if (!selectedCustomer) {
      toast.error('Selecione um cliente');
      return;
    }

    const amountToPay = payments.length > 0 ? remainingAmount : saleTotal;

    // Check credit limit
    if (selectedCustomer.available_credit !== null && amountToPay > selectedCustomer.available_credit) {
      toast.error(`Limite de crédito excedido. Disponível: ${formatCurrency(selectedCustomer.available_credit)}`);
      return;
    }

    const entry: PaymentEntry = {
      id: Date.now().toString(),
      method: 'fiado',
      amount: amountToPay,
    };
    const newPayments = [...payments, entry];
    setPayments(newPayments);
    await finalizeSale(newPayments);
  }, [selectedCustomer, payments, remainingAmount, saleTotal]);

  const handleAddMixedPayment = useCallback(() => {
    setStep('select');
    setSelectedMethod(null);
    setCashReceived('');
    setPixData(null);
  }, []);

  const finalizeSale = useCallback(async (finalPayments: PaymentEntry[]) => {
    if (!saleId) {
      toast.error('Erro: venda não encontrada. Tente novamente.');
      return;
    }
    setIsProcessing(true);

    try {
      const response = await apiFetch(`/api/sales/${saleId}/finalize`, {
        method: 'POST',
        body: JSON.stringify({
          payments: finalPayments.map((p) => ({
            method: p.method,
            amount: p.amount,
            received_amount: p.received_amount,
          })),
          customer_id: selectedCustomer?.id || undefined,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        toast.error(err.error || 'Erro ao finalizar venda');
        setIsProcessing(false);
        return;
      }

      const data = await response.json();
      setChange(data.change || 0);
      setReceiptNumber(data.receipt_number);

      // Fetch sale items for the receipt
      let saleItems: Array<{ product_name: string; quantity: number; unit_price: number; subtotal: number; unit: string }> = [];
      let saleSubtotal = data.total;
      let saleDiscount = 0;
      try {
        const saleResponse = await apiFetch(`/api/sales/${saleId}`);
        if (saleResponse.ok) {
          const saleData = await saleResponse.json();
          saleItems = (saleData.items || []).map((item: Record<string, unknown>) => ({
            product_name: (item.product_name as string) || 'Produto',
            quantity: Number(item.quantity),
            unit_price: Number(item.unit_price),
            subtotal: Number(item.subtotal),
            unit: (item.unit as string) || 'un',
          }));
          saleSubtotal = Number(saleData.subtotal) || data.total;
          saleDiscount = Number(saleData.desconto) || 0;
        }
      } catch {
        // Non-critical - receipt will still work without items
      }

      setReceiptData({
        sale_number: data.sale_number,
        receipt_number: data.receipt_number,
        total: data.total,
        change: data.change || 0,
        payments: (data.payments || []).map((p: { method: string; amount: number }) => ({
          method: p.method,
          amount: Number(p.amount),
        })),
        type: data.type,
        items: saleItems,
        subtotal: saleSubtotal,
        discount: saleDiscount,
      });
      setStep('success');
    } catch {
      toast.error('Erro de conexão');
      setIsProcessing(false);
    }
  }, [saleId, selectedCustomer]);

  const handlePrintReceipt = useCallback(() => {
    if (!receiptData) return;

    const now = new Date();
    const dateStr = now.toLocaleDateString('pt-BR');
    const timeStr = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    // Get establishment info from localStorage
    const tenantName = localStorage.getItem('nexopdv_tenant_name') || localStorage.getItem('tenant_name') || 'Meu Estabelecimento';
    const logo = localStorage.getItem('nexopdv_logo') || '';

    const paymentLines = receiptData.payments
      .map((p) => `<tr><td>${METHOD_LABELS[p.method as PaymentMethod] || p.method}</td><td style="text-align:right">R$ ${p.amount.toFixed(2)}</td></tr>`)
      .join('');

    const itemLines = (receiptData.items || [])
      .map((item, idx) => {
        const qtyStr = item.unit !== 'un' ? item.quantity.toFixed(3) : String(item.quantity);
        return `<tr>
          <td colspan="2" style="padding-top:4px"><small>${String(idx + 1).padStart(2, '0')}</small> ${item.product_name}</td>
        </tr>
        <tr>
          <td style="padding-left:20px"><small>${qtyStr} x R$ ${item.unit_price.toFixed(2)}</small></td>
          <td style="text-align:right">R$ ${item.subtotal.toFixed(2)}</td>
        </tr>`;
      })
      .join('');

    const subtotal = receiptData.subtotal ?? receiptData.total;
    const discount = receiptData.discount ?? 0;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cupom ${receiptData.receipt_number}</title>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 12px; width: 280px; margin: 0 auto; padding: 10px; }
          .center { text-align: center; }
          .divider { border-top: 1px dashed #000; margin: 8px 0; }
          table { width: 100%; border-collapse: collapse; }
          td { padding: 2px 0; font-size: 11px; }
          .bold { font-weight: bold; }
          .total { font-size: 14px; font-weight: bold; }
          .logo { max-width: 80px; max-height: 80px; margin: 0 auto 8px; display: block; }
          @media print { body { width: auto; } }
        </style>
      </head>
      <body>
        <div class="center">
          ${logo ? `<img src="${logo}" class="logo" alt="Logo" />` : ''}
          <strong>${tenantName}</strong><br/>
          <strong>CUPOM N-fO FISCAL</strong><br/>
          <small>${dateStr} ${timeStr}</small>
        </div>
        <div class="divider"></div>
        <table>
          <tr><td>Venda Nº</td><td style="text-align:right">${receiptData.sale_number}</td></tr>
          <tr><td>Cupom</td><td style="text-align:right">${receiptData.receipt_number}</td></tr>
        </table>
        <div class="divider"></div>
        ${itemLines ? `
          <div class="center bold">ITENS</div>
          <table>${itemLines}</table>
          <div class="divider"></div>
        ` : ''}
        <table>
          <tr><td>Subtotal</td><td style="text-align:right">R$ ${subtotal.toFixed(2)}</td></tr>
          ${discount > 0 ? `<tr><td>Desconto</td><td style="text-align:right">-R$ ${discount.toFixed(2)}</td></tr>` : ''}
          <tr class="total"><td>TOTAL</td><td style="text-align:right">R$ ${receiptData.total.toFixed(2)}</td></tr>
        </table>
        <div class="divider"></div>
        <div class="center bold">PAGAMENTO</div>
        <table>${paymentLines}</table>
        ${receiptData.change > 0 ? `<table><tr><td>Troco</td><td style="text-align:right">R$ ${receiptData.change.toFixed(2)}</td></tr></table>` : ''}
        <div class="divider"></div>
        <div class="center">
          <strong>PROGRAMA DE FIDELIDADE</strong><br/>
          ${selectedCustomer
            ? `<span>Pontos acumulados nesta compra: ${Math.floor(receiptData.total / 10)}</span><br/>
               <span>Seu saldo: ${Math.floor(receiptData.total / 10)} pontos</span><br/>`
            : `<span>Cadastre-se para acumular pontos!</span><br/>`}
          <small>Resgate a partir de 100 pontos!</small>
        </div>
        <div class="divider"></div>
        <div class="center"><small>Obrigado pela preferência!</small></div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;

    // Save receipt HTML for reprint
    try {
      localStorage.setItem('nexopdv_last_receipt', html);
    } catch {
      // localStorage full or unavailable
    }

    // Print via QZ Tray (silent) or fallback to browser dialog
    // If payment includes credit (fiado), print two copies - one for signature
    const isCreditSale = receiptData.payments.some(p => p.method === 'fiado');
    
    if (isCreditSale) {
      // Build second copy with signature line
      const signatureCopy = html.replace(
        '<div class="center"><small>Obrigado pela preferência!</small></div>',
        `<div class="divider"></div>
        <div class="center bold">COMPROVANTE DE CREDITO</div>
        <br/><br/>
        <div style="border-bottom:1px solid #000;margin:20px 0 5px;"></div>
        <div class="center"><small>Assinatura do Cliente</small></div>
        <br/>
        <div style="border-bottom:1px solid #000;margin:10px 0 5px;"></div>
        <div class="center"><small>Nome: ___________________________</small></div>
        <br/>
        <div class="center"><small>CPF: ___________________________</small></div>
        <br/>
        <div class="center"><small>Data: ${new Date().toLocaleDateString('pt-BR')}</small></div>
        <div class="divider"></div>
        <div class="center"><small>2a VIA - ESTABELECIMENTO</small></div>`
      );
      
      // Print first copy (client)
      const clientCopy = html.replace(
        '<div class="center"><small>Obrigado pela preferência!</small></div>',
        '<div class="center"><small>1a VIA - CLIENTE</small></div><div class="divider"></div><div class="center"><small>Obrigado pela preferência!</small></div>'
      );
      
      printReceipt(clientCopy + '<div style="page-break-after:always;"></div>' + signatureCopy);
    } else {
      printReceipt(html);
    }
  }, [receiptData, selectedCustomer]);

  // Save receipt to localStorage on success (even without printing)
  useEffect(() => {
    if (step === 'success' && receiptData) {
      try {
        localStorage.setItem('nexopdv_last_receipt', JSON.stringify(receiptData));
      } catch {
        // localStorage full or unavailable
      }
    }
  }, [step, receiptData]);

  // Auto-print receipt after success -?" prints directly without dialog when possible
  useEffect(() => {
    if (step === 'success' && receiptData) {
      const autoPrint = localStorage.getItem('nexopdv_auto_print') !== 'false';
      if (autoPrint) {
        // Small delay to let the success animation play
        const timer = setTimeout(() => {
          handlePrintReceipt();
        }, 800);
        return () => clearTimeout(timer);
      }
    }
  }, [step, receiptData, handlePrintReceipt]);

  const handleSuccessClose = useCallback(() => {
    onSuccess({ receipt_number: receiptNumber, change });
  }, [receiptNumber, change, onSuccess]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        onClick={(e) => { if (e.target === e.currentTarget && step !== 'success') onClose(); }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative w-full max-w-lg rounded-2xl border border-border/50 bg-card p-6 shadow-2xl"
        >
          {/* Close button */}
          {step !== 'success' && (
            <button
              onClick={onClose}
              className="absolute right-4 top-4 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          )}

          {/* Header */}
          {step !== 'success' && (
            <div className="mb-6">
              <h2 className="text-xl font-bold text-foreground">Pagamento</h2>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total da venda</span>
                <span className="text-lg font-bold text-primary">{formatCurrency(saleTotal)}</span>
              </div>
              {payments.length > 0 && (
                <div className="mt-2 flex items-center justify-between rounded-lg bg-muted/30 px-3 py-2">
                  <span className="text-sm text-muted-foreground">Restante</span>
                  <span className="text-base font-semibold text-foreground">
                    {formatCurrency(Math.max(0, remainingAmount))}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Mixed payments list */}
          {payments.length > 0 && step !== 'success' && (
            <div className="mb-4 space-y-2">
              {payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-lg bg-muted/20 px-3 py-2 text-sm">
                  <span className="text-muted-foreground">{METHOD_LABELS[p.method]}</span>
                  <span className="font-medium text-foreground">{formatCurrency(p.amount)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Step: Select payment method */}
          {step === 'select' && (
            <div className="grid grid-cols-2 gap-3">
              {(Object.keys(METHOD_LABELS) as PaymentMethod[]).map((method) => {
                const isPixOffline = method === 'pix' && !navigator.onLine;
                return (
                  <button
                    key={method}
                    onClick={() => !isPixOffline && handleMethodSelect(method)}
                    disabled={isPixOffline}
                    className={`flex flex-col items-center gap-2 rounded-xl border border-border/50 bg-card p-4 transition-all ${
                      isPixOffline
                        ? 'opacity-40 cursor-not-allowed'
                        : 'hover:border-primary/50 hover:bg-muted/30 hover:shadow-[0_0_15px_hsl(160_84%_39%/0.15)]'
                    }`}
                  >
                    <span className="text-primary">{METHOD_ICONS[method]}</span>
                    <span className="text-sm font-medium text-foreground">{METHOD_LABELS[method]}</span>
                    {isPixOffline && <span className="text-[10px] text-muted-foreground">Indisponível offline</span>}
                  </button>
                );
              })}
              {payments.length > 0 && remainingAmount <= 0 && (
                <button
                  onClick={() => finalizeSale(payments)}
                  className="col-span-2 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 py-3 font-bold text-white"
                >
                  <Check className="h-5 w-5" />
                  Finalizar
                </button>
              )}
            </div>
          )}

          {/* Step: Cash payment */}
          {step === 'cash' && (
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-muted-foreground">
                  Valor recebido
                </label>
                <input
                  ref={cashInputRef}
                  type="text"
                  inputMode="decimal"
                  value={cashReceived}
                  onChange={(e) => setCashReceived(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCashPayment(); }}
                  placeholder="0,00"
                  className="w-full rounded-xl border border-border bg-muted/30 px-4 py-3 text-center text-2xl font-bold text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              {cashReceived && (() => {
                const received = parseFloat(cashReceived.replace(',', '.'));
                const amountToPay = payments.length > 0 ? remainingAmount : saleTotal;
                const changeCalc = received - amountToPay;
                if (!isNaN(received) && changeCalc > 0) {
                  return (
                    <div className="rounded-lg bg-green-500/10 px-4 py-3 text-center">
                      <span className="text-sm text-muted-foreground">Troco: </span>
                      <span className="text-lg font-bold text-green-400">{formatCurrency(changeCalc)}</span>
                    </div>
                  );
                }
                return null;
              })()}

              <div className="flex gap-3">
                <button
                  onClick={() => { setStep('select'); setCashReceived(''); }}
                  className="flex-1 rounded-xl border border-border py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted"
                >
                  Voltar
                </button>
                <button
                  onClick={handleCashPayment}
                  disabled={isProcessing}
                  className="flex-1 rounded-xl bg-gradient-to-r from-primary to-secondary py-3 text-sm font-bold text-white transition-all hover:scale-[1.02] disabled:opacity-50"
                >
                  {isProcessing ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : 'Confirmar'}
                </button>
              </div>
            </div>
          )}

          {/* Step: Card payment */}
          {step === 'card' && (
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <CreditCard className="h-8 w-8 text-primary" />
              </div>
              <p className="text-lg font-medium text-foreground">
                {selectedMethod === 'pix' ? 'Confirmar pagamento PIX' : 'Confirmar pagamento na maquininha'}
              </p>
              <p className="text-sm text-muted-foreground">
                {formatCurrency(payments.length > 0 ? remainingAmount : saleTotal)} via{' '}
                {selectedMethod === 'cartao_debito' ? 'Débito' : selectedMethod === 'pix' ? 'PIX' : 'Crédito'}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep('select')}
                  className="flex-1 rounded-xl border border-border py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted"
                >
                  Voltar
                </button>
                <button
                  onClick={handleCardPayment}
                  disabled={isProcessing}
                  className="flex-1 rounded-xl bg-gradient-to-r from-primary to-secondary py-3 text-sm font-bold text-white transition-all hover:scale-[1.02] disabled:opacity-50"
                >
                  {isProcessing ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : 'Pagamento Confirmado'}
                </button>
              </div>
            </div>
          )}

          {/* Step: PIX payment */}
          {step === 'pix' && (
            <div className="space-y-4 text-center">
              {pixData ? (
                <>


                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>
                      Expira em {Math.floor(pixTimer / 60)}:{String(pixTimer % 60).padStart(2, '0')}
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    Valor: <span className="font-bold text-foreground">{formatCurrency(payments.length > 0 ? remainingAmount : saleTotal)}</span>
                  </p>



                  <div className="flex gap-3">
                    <button
                      onClick={() => { setStep('select'); setPixData(null); }}
                      className="flex-1 rounded-xl border border-border py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted"
                    >
                      Voltar
                    </button>
                    <button
                      onClick={handlePixConfirm}
                      disabled={isProcessing}
                      className="flex-1 rounded-xl bg-gradient-to-r from-primary to-secondary py-3 text-sm font-bold text-white transition-all hover:scale-[1.02] disabled:opacity-50"
                    >
                      {isProcessing ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : 'Confirmar Manualmente'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center gap-3 py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Gerando QR Code...</p>
                </div>
              )}
            </div>
          )}

          {/* Step: Fiado */}
          {step === 'fiado' && (
            <div className="space-y-4">
              {selectedCustomer ? (
                <>
                  <div className="rounded-xl border border-border/50 bg-muted/20 p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                        <UserCheck className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{selectedCustomer.name}</p>
                        <p className="text-sm text-muted-foreground">
                          Crédito disponível:{' '}
                          <span className="font-medium text-foreground">
                            {selectedCustomer.available_credit === null
                              ? 'Ilimitado'
                              : formatCurrency(selectedCustomer.available_credit)}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {selectedCustomer.available_credit !== null &&
                    (payments.length > 0 ? remainingAmount : saleTotal) > selectedCustomer.available_credit && (
                    <div className="flex items-center gap-2 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400">
                      <AlertTriangle className="h-4 w-4" />
                      <span>Limite de crédito insuficiente</span>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => { setStep('select'); setSelectedCustomer(null); }}
                      className="flex-1 rounded-xl border border-border py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted"
                    >
                      Voltar
                    </button>
                    <button
                      onClick={handleFiadoConfirm}
                      disabled={isProcessing || (selectedCustomer.available_credit !== null && (payments.length > 0 ? remainingAmount : saleTotal) > selectedCustomer.available_credit)}
                      className="flex-1 rounded-xl bg-gradient-to-r from-primary to-secondary py-3 text-sm font-bold text-white transition-all hover:scale-[1.02] disabled:opacity-50"
                    >
                      {isProcessing ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : 'Confirmar Credito'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center gap-3 py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Buscando cliente...</p>
                </div>
              )}
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', damping: 15, stiffness: 200 }}
              className="flex flex-col items-center gap-4 py-6"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', damping: 10, stiffness: 200 }}
                className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20"
              >
                <Check className="h-8 w-8 text-green-400" />
              </motion.div>

              <h3 className="text-xl font-bold text-foreground">Venda Finalizada!</h3>

              {change > 0 && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3, type: 'spring', damping: 12, stiffness: 180 }}
                  className="relative w-full rounded-2xl border border-green-500/30 bg-green-500/10 px-6 py-5 text-center shadow-[0_0_30px_hsl(142_70%_45%/0.2)]"
                >
                  <span className="block text-sm font-semibold uppercase tracking-wider text-green-300/80">TROCO</span>
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.5, type: 'spring', damping: 10, stiffness: 150 }}
                    className="mt-1 block text-6xl font-bold text-green-400"
                  >
                    {formatCurrency(change)}
                  </motion.span>
                </motion.div>
              )}

              {/* Receipt details */}
              {receiptData && (
                <div className="w-full space-y-2 rounded-xl border border-border/50 bg-muted/20 p-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Venda Nº</span>
                    <span className="font-medium text-foreground">{receiptData.sale_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cupom</span>
                    <span className="font-mono text-foreground">{receiptData.receipt_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total</span>
                    <span className="font-medium text-foreground">{formatCurrency(receiptData.total)}</span>
                  </div>
                  {receiptData.payments.length > 0 && (
                    <>
                      <div className="my-1 border-t border-border/30" />
                      {receiptData.payments.map((p, idx) => (
                        <div key={idx} className="flex justify-between">
                          <span className="text-muted-foreground">
                            {METHOD_LABELS[p.method as PaymentMethod] || p.method}
                          </span>
                          <span className="text-foreground">{formatCurrency(p.amount)}</span>
                        </div>
                      ))}
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Data/Hora</span>
                    <span className="text-foreground">
                      {new Date().toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex w-full gap-3">
                <button
                  onClick={handlePrintReceipt}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-border py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                >
                  <Printer className="h-4 w-4" />
                  Imprimir Cupom
                </button>
                <button
                  onClick={handleSuccessClose}
                  className="flex-1 rounded-xl bg-gradient-to-r from-primary to-secondary py-3 text-sm font-bold text-white transition-all hover:scale-[1.02]"
                >
                  Nova Venda
                </button>
              </div>

              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  defaultChecked={localStorage.getItem('nexopdv_auto_print') !== 'false'}
                  onChange={(e) => localStorage.setItem('nexopdv_auto_print', String(e.target.checked))}
                  className="rounded border-border"
                />
                Impressão automática
              </label>
            </motion.div>
          )}
        </motion.div>

        {/* Customer Search Modal */}
        <CustomerSearchModal
          isOpen={customerSearch}
          onClose={() => {
            setCustomerSearch(false);
            if (!selectedCustomer) setStep('select');
          }}
          onSelect={(customer) => {
            setSelectedCustomer({
              id: customer.id,
              name: customer.full_name,
              available_credit: customer.available_credit,
            });
            setCustomerSearch(false);
          }}
        />
      </motion.div>
    </AnimatePresence>
  );
}
